

import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { decryptToken } from "../Utlis/Encryption";
import { BASE_URL } from "./BaseUrl";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";

const getAlertMessage = (alert, isSpanish, detailedStatus, daysRemaining, t) => {
  if (alert) {
    const localized = isSpanish ? alert.message_es : alert.message_en;
    return localized || alert.message || alert.message_en || alert.message_es;
  }

  if (detailedStatus === "due_soon" && typeof daysRemaining === "number") {
    return t("subscriptionPage.banner_renews_in_days", { count: daysRemaining });
  }

  if (detailedStatus === "subscription_suspended") {
    return isSpanish
      ? "Tu renovación falló. Actualiza tu método de pago para restaurar la suscripción."
      : "Your renewal failed. Please update your payment method to restore your subscription.";
  }

  if (detailedStatus === "subscription_cancelled" || detailedStatus === "cancelled") {
    return isSpanish
      ? "Tu suscripción ha sido cancelada. Suscríbete a un nuevo plan para seguir usando las funciones premium."
      : "Your subscription has been cancelled. Please subscribe to a new plan to continue using premium features.";
  }

  if (detailedStatus === "subscription_expired" || detailedStatus === "expired") {
    return isSpanish
      ? "Tu suscripción ha expirado. Suscríbete de nuevo para continuar."
      : "Your subscription has expired. Please subscribe again to continue.";
  }

  if (detailedStatus === "blocked") {
    return isSpanish
      ? "Tu prueba ha finalizado. Actualiza tu plan para seguir usando las funciones premium."
      : "Your trial has ended. Please upgrade your plan to continue using premium features.";
  }

  return isSpanish
    ? "Tu plan tiene un problema. Por favor actualiza."
    : "Your plan has an issue. Please upgrade.";
};

const STATUS_WITH_ACTION = new Set([
  "warning",
  "critical",
  "expired",
  "payment_overdue",
  "cancelled",
  "subscription_suspended",
  "subscription_cancelled",
  "subscription_expired",
  "blocked",
  "due_soon",
]);

const GlobalBannerAlert = ({ detailedStatus, alert, cta, daysRemaining }) => {
  const { t, i18n } = useTranslation();
  const isSpanish = i18n.language?.startsWith("es");
  const message = getAlertMessage(alert, isSpanish, detailedStatus, daysRemaining, t);

  const getButtonLabel = () => {
    if (cta === "renew" || detailedStatus === "payment_overdue") {
      return t("subscriptionPage.renew_subscription");
    }
    if (cta === "subscribe" || detailedStatus === "cancelled" || detailedStatus === "subscription_cancelled") {
      return t("subscriptionPage.subscribe_now");
    }
    if (cta === "update_payment" || detailedStatus === "subscription_suspended") {
      return t("subscriptionPage.change_payment_method");
    }
    if (cta === "view_billing" || detailedStatus === "due_soon") {
      return t("subscriptionPage.view_billing");
    }
    return t("subscriptionPage.upgrade");
  };

  let bgColor = "#2196f3";
  let emoji = "ℹ️";

  switch (detailedStatus) {
    case "critical":
    case "subscription_expired":
      bgColor = "#DC2626";
      emoji = "🚨";
      break;
    case "warning":
      bgColor = "#F59E0B";
      emoji = "⚠️";
      break;
    case "payment_overdue":
    case "subscription_suspended":
      bgColor = "#EA580C";
      emoji = "⚠️";
      break;
    case "expired":
      bgColor = "#DC2626";
      emoji = "🕒";
      break;
    case "cancelled":
    case "subscription_cancelled":
      bgColor = "#B91C1C";
      emoji = "❌";
      break;
    case "blocked":
      bgColor = "#7C3AED";
      emoji = "🔒";
      break;
    case "due_soon":
      bgColor = "#0284C7";
      emoji = "ℹ️";
      break;
    default:
      bgColor = "#2196f3";
      emoji = "ℹ️";
  }

  const navigate = useNavigate();

  const handleViewBillingClick = () => {
    navigate("/subscription-plans");
  };

  const handleUpgradeClick = () => {
    navigate("/subscription-plans", { state: { activeTab: "upgrade_plans" } });
  };

  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;

  const handleActionClick = async () => {
    const action = cta || (
      detailedStatus === "payment_overdue"
        ? "renew"
        : detailedStatus === "subscription_suspended"
          ? "update_payment"
          : detailedStatus === "due_soon"
            ? "view_billing"
            : ["cancelled", "subscription_cancelled", "subscription_expired", "expired"].includes(detailedStatus)
              ? "subscribe"
              : "upgrade"
    );

    if (
      action === "view_billing" ||
      detailedStatus === "due_soon" ||
      action === "update_payment" ||
      detailedStatus === "subscription_suspended"
    ) {
      handleViewBillingClick();
      return;
    }

    try {
      if (action === "renew" || detailedStatus === "payment_overdue") {
        const res = await fetch(
          `${BASE_URL}/payments/company-admin/generate-payment-link`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${decryptedToken}`,
            },
          }
        );

        const json = await res.json();
        if (!res.ok || json.error) throw new Error(json.message);

        if (json?.data?.paypal_url) {
          window.location.href = json.data.paypal_url;
        } else {
          toast.error("Payment link not received");
        }
        return;
      }

      navigate("/subscription-plans", { state: { activeTab: "upgrade_plans" } });
    } catch (err) {
      console.error("Payment banner action error:", err);
      toast.error(err.message || t("subscriptionPage.something_went_wrong"));
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2,
        py: 1.2,
        backgroundColor: bgColor,
        color: "#fff",
        boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box>{emoji}</Box>
        <Typography
          sx={{
            fontSize: "14px",
            fontFamily: "Poppins, sans-serif",
          }}
        >
          {message}{" "}
          {(detailedStatus === "expired" ||
            detailedStatus === "subscription_expired" ||
            detailedStatus === "blocked" ||
            detailedStatus === "cancelled" ||
            detailedStatus === "subscription_cancelled") && (
            <span
              onClick={handleUpgradeClick}
              style={{
                cursor: "pointer",
                color: "#fff",
                fontSize: "13px",
                marginLeft: "6px",
              }}
            />
          )}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {STATUS_WITH_ACTION.has(detailedStatus) && (
          <Button
            variant="contained"
            size="small"
            sx={{
              fontFamily: "Poppins, sans-serif",
              backgroundColor: "#fff",
              color: bgColor,
              textTransform: "none",
              fontSize: "12px",
              fontWeight: 500,
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
            onClick={handleActionClick}
          >
            {getButtonLabel()}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default GlobalBannerAlert;
